import {TextUnderCursor} from '@frontapp/editor-html';
import {useRef} from 'react';

import {Handler, HandlerOf} from '../../../../../../libs/shared/helpers/src/react/reactHelpers';

export const COMMAND_START_TOKEN = '/';

export const createTextIsBlankAt = (text: string) => (position: number) => {
  const code = text.charCodeAt(position);
  return (
    // the position is out of range
    isNaN(code) ||
    // space
    code === 32 ||
    // nbps
    code === 160
  );
};

/**
 * Get the user command "input" i.e. the text in front of the initial "/" that
 * opened the command dropdown.
 */
export function computeInput(textUnderCursor: TextUnderCursor): string {
  const {text, anchorOffset} = textUnderCursor;
  const textIsBlankAt = createTextIsBlankAt(text);

  const chars: string[] = [];

  for (let i = anchorOffset - 1; i >= 0; i--) {
    if (textIsBlankAt(i)) {
      break;
    }
    chars.unshift(text.charAt(i));
  }

  if (chars[0] === COMMAND_START_TOKEN) {
    chars.shift();
  }

  return chars.join('').toLowerCase();
}

/**
 * Resolve `InteractiveList`'s "close" into "cancel" and "select".
 *
 * The dropdowns/popovers used in quick insert are backed by `InteractiveList`,
 * which receives an `onRequestClose` prop that it calls whenever the list
 * should be closed, irrespective of whether an item was selected or the
 * interaction was "cancelled", by, say, clicking away or hitting ESC:
 * https://github.com/frontapp/front-client/blob/39b3ad79a43649a9ba3228e0eb980d7b8e94b6af/libs/shared/ui/src/components/interactiveList/interactiveList.tsx#L173-L187.
 * This hooks enables differentiating between calls to `onRequestClose()` that
 * resulted from an item selection and those caused by cancellation.
 *
 * @param onCancel the callback to call only when the list should be closed
 * because the interaction was cancelled (no item was selected).
 * @param onSelect the callback to call when an item was selected.
 * @returns an `onRequestClose` to pass to the `InteractiveList`; and `onSelect`
 * to use when selecting an item.
 */
export const useInteractiveListOnCancel = <A>(
  onCancel: Handler,
  onSelect: HandlerOf<A>,
): [Handler, HandlerOf<A>] => {
  const didSelect = useRef(false);

  const wrappedOnSelect = (a: A) => {
    didSelect.current = true;
    onSelect(a);
  };

  const onRequestClose = () => {
    if (didSelect.current) {
      return;
    }
    onCancel();
  };

  return [onRequestClose, wrappedOnSelect];
};
